<template>
  <div>

    <!-- Add Event Button -->
    <b-button variant="primary" @click="$bvModal.show('addOrEditEventModal')" size="xl">
      Add New Event
    </b-button>

    <!-- Event Coordinators Modal -->
    <b-modal id="eventCoordinators" :title="eventCoordinatorsModal.name + ': Event Co-ordinators'" no-close-on-backdrop
      no-close-on-keyboard no-close-on-esc hide-footer centered size="lg" @close="checkCoordinatorStatus">
      <EventCoordinators @coordinatorsRemove="coordinatorsRemove" @newEventCoordinatorAdded="fetchEvents()"
        :eventCoordinatorsModal="eventCoordinatorsModal" :faculties="faculties" />
    </b-modal>

    <br><br>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary" class="mr-2"></b-spinner>
      <span class="font-weight-bold">Loading event data...</span>
    </div>

    <!-- Table -->
    <div v-else>
      <table class="table table-striped table-bordered">
        <thead class="thead-dark text-left">
          <tr>
            <th>SI. No.</th>
            <th>Name of Event</th>
            <th>From Date</th>
            <th>To Date</th>
            <th>Academic year</th>
            <th>Staff Co-ordinator</th>
            <th>{{ customName }}s</th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(ev, index) in eventList" :key="ev.id">

            <td>{{ index + 1 }}</td>
            <td>{{ ev.name }}</td>
            <td>{{ formatDate(ev.from_date) }}</td>
            <td>{{ formatDate(ev.to_date) }}</td>
            <td>{{ ev.academic_year }} - {{ ev.semester  }}</td>

            <!-- Staff Co-ordinators -->
            <td class="align-middle">
              <b-popover :target="'courseAdmin' + index" triggers="hover" placement="top"
                v-if="ev.faculties && ev.faculties.length">
                <b-list-group style="height: 200px; overflow-y: auto">
                  <b-list-group-item v-for="(f, i) in ev.faculties" :key="i">
                    <span :class="{ 'text-danger': f.enroll_status !== 'active' }">
                      {{ f.first_name }} {{ f.last_name }}
                    </span>
                  </b-list-group-item>
                </b-list-group>
              </b-popover>

              <span :id="'courseAdmin' + index" class="text-info btn p-0 m-0"
                @click="getEventCoordinators(ev.id, ev.name, ev.faculties)">
                <span v-if="ev.faculties && ev.faculties.length">
                  {{ ev.faculties[0].first_name }} {{ ev.faculties[0].last_name }}

                  <span v-if="ev.faculties.length > 1">
                    & {{ ev.faculties.length - 1 }} other
                  </span>
                </span>

                <span v-else class="text-danger btn">
                  No Co-ordinators Assigned!
                </span>
              </span>
            </td>

            <!-- Main Groups -->
            <td>
              {{(ev.maingroups || []).map(mg => mg.code || mg.name).join(', ')}}
            </td>

            <td>
              <i class="far fa-edit text-info" style="cursor:pointer" @click="editEvent(ev)">
              </i>
            </td>

          </tr>

          <tr v-if="eventList.length === 0">
            <td colspan="7" class="text-center text-muted">No events found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Add/Edit Event Modal -->
    <b-modal id="addOrEditEventModal" no-close-on-backdrop no-close-on-keyboard hide-footer hide-header centered
      size="xl">
      <div>

        <!-- Event Name -->
        <label>Name of Event <span class="text-danger">*</span></label>
        <b-form-input v-model="event.name" type="text"></b-form-input>
        <span class="text-danger" v-if="$v.event.name.$error">Event name is required.</span>
        <br>

        <!-- From Date -->
        <label>From Date <span class="text-danger">*</span></label>
        <b-form-datepicker v-model="event.from_date"></b-form-datepicker>
        <span class="text-danger" v-if="$v.event.from_date.$error">From date is required.</span>
        <br>

        <!-- To Date -->
        <label>To Date <span class="text-danger">*</span></label>
        <b-form-datepicker v-model="event.to_date"></b-form-datepicker>
        <span class="text-danger" v-if="$v.event.to_date.$error">To date is required.</span>
        <br>

        <!-- All Departments -->
        <b-form-checkbox v-model="event.all_departments" @change="toggleDepartments">
          All Institutes
        </b-form-checkbox>
        <br>

        <!-- Main Group List -->
        <label>Select {{ customName }}s</label>
        <div class="border p-3" style="max-height: 200px; overflow-y: auto;" :disabled="event.all_departments">
          <b-form-checkbox-group v-model="event.maingroups">
            <b-form-checkbox  :disabled="event.all_departments" v-for="mg in departmentsList" :key="mg.id" :value="mg.id">
              {{ mg.name }}
            </b-form-checkbox>
          </b-form-checkbox-group>
        </div>

        <br>

        <!-- Buttons -->
        <div class="text-center mt-3">
          <b-button variant="success" size="sm" class="mr-2 w-25" @click="saveEvent">
            {{ event.id ? 'Update' : 'Submit' }}
          </b-button>


          <b-button variant="danger" size="sm" class="w-25" @click="closeModal">
            Cancel
          </b-button>
        </div>

      </div>
    </b-modal>

  </div>
</template>

<script>
import { required } from "vuelidate/lib/validators";
import EventCoordinators from "./EventCoordinators.vue";
import axios from "axios";

export default {
  data() {
    return {
      loading: true,

      eventCoordinatorsModal: {},

      event: {
        name: null,
        from_date: null,
        to_date: null,
        all_departments: false,
        maingroups: []
      },

      faculties: [],
      departmentsList: [],
      eventList: []
    };
  },

  components: {
    EventCoordinators
  },

  validations() {
    return {
      event: {
        name: { required },
        from_date: { required },
        to_date: { required }
      }
    };
  },

  async created() {
    await this.loadMainGroups();
    this.toggleDepartments();
    await this.fetchEvents();
    await this.getInstitutionFaculties();
  },

  computed: {
    getCustomNames() {
      return this.$store.getters.getCustomNamings;
    },
    customName() {
      return  this.getCustomNames.mainGroup.custom_name;
    },
  },

  methods: {
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    },

    editEvent(ev) {
      this.event = {
        name: ev.name,
        from_date: ev.from_date,
        to_date: ev.to_date,
        all_departments: ev.all_departments == 1 || ev.all_departments === true,
        maingroups: (ev.maingroups || []).map(m => m.id)
      };

      this.event.id = ev.id; // store id for update
      this.$bvModal.show("addOrEditEventModal");
    },

    checkCoordinatorStatus() {
      if (
        Number(this.eventCoordinatorsModal.coordinators.length) !==
        Number(this.eventCoordinatorsModal.remainingCoordinators)
      ) {
        this.fetchEvents();
      }
    },

    coordinatorsRemove(remaining) {
      this.eventCoordinatorsModal.remainingCoordinators = remaining;
    },

    async getInstitutionFaculties() {
      const url = this.$store.getters.getAPIKey.instituitionFaculty;

      await axios
        .get(url)
        .then((res) => {
          this.faculties = res.data;
        })
        .catch(() => { });
    },

    getEventCoordinators(eventId, eventName, eventCoordinators) {
      this.eventCoordinatorsModal = {
        id: eventId,
        name: eventName,
        coordinators: eventCoordinators,
        totalCoordinators: eventCoordinators.length,
        remainingCoordinators: eventCoordinators.length
      };

      this.$bvModal.show("eventCoordinators");
    },

    async loadMainGroups() {
      const url =
        this.$store.getters.getAPIKey.mainAPI +
        `/maingroups?with_admins=1&with_po_count=1&with_users_count=1`;

      await this.$axios
        .get(url)
        .then((res) => {
          this.departmentsList = res.data;
        })
        .catch((err) => console.error(err));
    },

    toggleDepartments() {
      if (this.event.all_departments) {
        this.event.maingroups = this.departmentsList.map((d) => d.id);
      } else {
        this.event.maingroups = [];
      }
    },

    async saveEvent() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      const baseUrl = this.$store.getters.getAPIKey.mainAPI + `/events`;

      if (this.event.id) {
        // UPDATE event
        await this.$axios
          .put(baseUrl + '/' + this.event.id, this.event)
          .then(() => {
            this.resetForm();
            this.$bvModal.hide("addOrEditEventModal");
            this.fetchEvents();
          });
      } else {
        // CREATE event (existing code)
        await this.$axios
          .post(baseUrl, this.event)
          .then(() => {
            this.resetForm();
            this.$bvModal.hide("addOrEditEventModal");
            this.fetchEvents();
          });
      }
    },

    async fetchEvents() {
      const url = this.$store.getters.getAPIKey.mainAPI + `/events`;

      await this.$axios
        .get(url)
        .then((res) => {
          this.eventList = res.data;
          this.loading = false;
        })
        .catch(() => { });
    },

    closeModal() {
      this.resetForm();
      this.$bvModal.hide("addOrEditEventModal");
    },

    resetForm() {
      this.event = {
        name: null,
        from_date: null,
        to_date: null,
        all_departments: false,
        maingroups: [],
        id: null
      };

      this.$v.$reset();
    }
  }
};
</script>

<style scoped>
.studentLeave .modal-dialog {
  max-width: 100%;
  max-height: 100vh;
  margin: 0;
  position: fixed;
  z-index: 100000;
}

.studentLeave .modal-body {
  overflow: scroll;
}
</style>
